import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api, apiClient } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  LogOut, Clock, AlertTriangle, CheckCircle, Save, 
  Loader2, AlertCircle, User, Check, Calendar, Trash2, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_lunch-roster-hub/artifacts/wio7p25o_rr%20logo.png';

const OrderForm = () => {
  const { groupAuth, logoutGroup, isGroupLoggedIn } = useAuth();
  const navigate = useNavigate();
  
  const [campers, setCampers] = useState([]);
  const [orders, setOrders] = useState({});
  const [savedOrders, setSavedOrders] = useState({});
  const [config, setConfig] = useState(null);
  const [staffMenuItems, setStaffMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [cutoffPassed, setCutoffPassed] = useState(false);
  const [timeUntilCutoff, setTimeUntilCutoff] = useState(0);
  const [allergyWarning, setAllergyWarning] = useState(null);
  const [overrideName, setOverrideName] = useState('');
  const [overrideError, setOverrideError] = useState('');
  const [overrides, setOverrides] = useState({});
  const [allergenData, setAllergenData] = useState(null);
  const [orderDate, setOrderDate] = useState(''); // empty = today
  const [counselorCitCount, setCounselorCitCount] = useState(0);
  const [counselorCitSaved, setCounselorCitSaved] = useState(false);

  useEffect(() => {
    if (!isGroupLoggedIn) {
      navigate('/');
    }
  }, [isGroupLoggedIn, navigate]);

  // Initial load: fetch campers, config, staff, and orders
  const fetchData = useCallback(async () => {
    if (!groupAuth?.group?.id) return;
    
    setLoading(true);
    try {
      const [campersRes, staffRes, configRes, orderRes, staffMenuRes] = await Promise.all([
        api.getCampersByGroup(groupAuth.group.id, orderDate || undefined),
        api.getStaffByGroup(groupAuth.group.id),
        api.getConfig(),
        api.getGroupOrder(groupAuth.group.id, orderDate || undefined),
        api.getStaffMenu()
      ]);

      // Merge staff (first) + campers, deduplicate by person_id
      const staffList = (staffRes.data || []).map(s => ({ ...s, is_staff: true }));
      const staffIds = new Set(staffList.map(s => s.person_id));
      const camperList = (campersRes.data || []).filter(c => !c.is_staff && !staffIds.has(c.person_id));
      setCampers([...staffList, ...camperList]);
      setConfig(configRes.data);
      setStaffMenuItems(staffMenuRes.data.menu_items || []);
      
      // Only enforce cutoff for today (not custom dates)
      if (orderDate) {
        setCutoffPassed(false);
        setTimeUntilCutoff(0);
      } else {
        setCutoffPassed(configRes.data.cutoff_active || false);
        setTimeUntilCutoff(configRes.data.time_until_cutoff);
      }

      // Load existing orders
      if (orderRes.data.order) {
        const orderMap = {};
        const savedMap = {};
        const ovMap = {};
        orderRes.data.order.orders.forEach(o => {
          orderMap[o.camper_id] = o.lunch_choice;
          savedMap[o.camper_id] = true;
          if (o.allergy_override_by) ovMap[o.camper_id] = { by: o.allergy_override_by, at: o.allergy_override_at };
        });
        setOrders(orderMap);
        setSavedOrders(savedMap);
        setOverrides(ovMap);
        // Load counselor/CIT count
        const citCount = orderRes.data.order.counselor_cit_count || 0;
        setCounselorCitCount(citCount);
        setCounselorCitSaved(citCount > 0);
      } else {
        setOrders({});
        setSavedOrders({});
        setOverrides({});
        setCounselorCitCount(0);
        setCounselorCitSaved(false);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load camper data');
    } finally {
      setLoading(false);
    }
  }, [groupAuth]); // Only depends on groupAuth, NOT orderDate

  // Initial load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load allergen blocking data (item map, keyword map, overrides)
  useEffect(() => {
    api.getAllergenData().then(res => setAllergenData(res.data)).catch(() => {});
  }, []);

  // When orderDate changes, refetch campers roster + orders for the new date
  const fetchOrdersForDate = useCallback(async (date) => {
    if (!groupAuth?.group?.id) return;
    try {
      // Fetch campers for the new date (CamperSnapshot roster is date-specific)
      const [campersRes, staffRes, orderRes] = await Promise.all([
        api.getCampersByGroup(groupAuth.group.id, date || undefined),
        api.getStaffByGroup(groupAuth.group.id),
        api.getGroupOrder(groupAuth.group.id, date || undefined)
      ]);

      const staffList = (staffRes.data || []).map(s => ({ ...s, is_staff: true }));
      const staffIds = new Set(staffList.map(s => s.person_id));
      const camperList = (campersRes.data || []).filter(c => !c.is_staff && !staffIds.has(c.person_id));
      setCampers([...staffList, ...camperList]);

      if (date) {
        setCutoffPassed(false);
        setTimeUntilCutoff(0);
      } else {
        const configRes = await api.getConfig();
        setCutoffPassed(configRes.data.cutoff_active || false);
        setTimeUntilCutoff(configRes.data.time_until_cutoff);
      }

      if (orderRes.data.order) {
        const orderMap = {};
        const savedMap = {};
        const ovMap = {};
        orderRes.data.order.orders.forEach(o => {
          orderMap[o.camper_id] = o.lunch_choice;
          savedMap[o.camper_id] = true;
          if (o.allergy_override_by) ovMap[o.camper_id] = { by: o.allergy_override_by, at: o.allergy_override_at };
        });
        setOrders(orderMap);
        setSavedOrders(savedMap);
        setOverrides(ovMap);
        setCounselorCitCount(orderRes.data.order.counselor_cit_count || 0);
        setCounselorCitSaved(!!orderRes.data.order.counselor_cit_count);
      } else {
        setOrders({});
        setSavedOrders({});
        setOverrides({});
        setCounselorCitCount(0);
        setCounselorCitSaved(false);
      }
    } catch (error) {
      console.error('Error fetching orders for date:', error);
    }
  }, [groupAuth]);

  // When date changes, fetch orders for that date without replacing the whole page
  const prevDateRef = React.useRef(orderDate);
  useEffect(() => {
    if (prevDateRef.current !== orderDate) {
      prevDateRef.current = orderDate;
      fetchOrdersForDate(orderDate);
    }
  }, [orderDate, fetchOrdersForDate]);

  // Countdown timer
  useEffect(() => {
    if (timeUntilCutoff <= 0) return;

    const interval = setInterval(() => {
      setTimeUntilCutoff(prev => {
        if (prev <= 1) {
          setCutoffPassed(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timeUntilCutoff]);

  const formatCountdown = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${secs}s`;
  };

  const getFoodOfDay = () => {
    // If a custom date is selected, look up the schedule for that date
    if (orderDate && config?.config?.daily_menu_schedule) {
      return config.config.daily_menu_schedule[orderDate] || null;
    }
    // Otherwise use today's food_of_day
    return config?.config?.food_of_day || null;
  };

  const getGroupDivision = () => {
    const code = groupAuth?.group?.name?.split(' ')[0]?.toUpperCase() || '';
    const divisions = {
      skippers: Array.from({length: 16}, (_, i) => `N${String(i+1).padStart(2,'0')}`),
      lower_mariners: [...Array.from({length: 7}, (_, i) => `B${String(i+1).padStart(2,'0')}`), ...Array.from({length: 7}, (_, i) => `G${String(i+1).padStart(2,'0')}`)],
      upper_mariners: [...Array.from({length: 4}, (_, i) => `B${String(i+8).padStart(2,'0')}`), ...Array.from({length: 4}, (_, i) => `G${String(i+8).padStart(2,'0')}`)],
      navigators: [...Array.from({length: 4}, (_, i) => `B${String(i+12).padStart(2,'0')}`), ...Array.from({length: 4}, (_, i) => `G${String(i+12).padStart(2,'0')}`)],
    };
    for (const [key, groups] of Object.entries(divisions)) {
      if (groups.includes(code)) return key;
    }
    return null;
  };

  const isGlutenFreeItem = (name) => {
    const upper = (name || '').toUpperCase();
    return upper.includes('GLUTEN FREE') || upper.includes('GLUTEN-FREE') || upper.includes('(GF)') || upper.startsWith('GF ');
  };

  const getMenuItems = (camper = null) => {
    const items = [];
    const foodOfDay = getFoodOfDay();
    
    // Lunch of the Day (hot food that changes daily)
    if (foodOfDay) {
      // Hide GF food-of-day from non-eligible campers
      if (!isGlutenFreeItem(foodOfDay) || !camper || camperNeedsGlutenFree(camper)) {
        items.push({
          value: foodOfDay,
          label: `Lunch of the Day - ${foodOfDay}`,
          category: 'Lunch of the Day'
        });
      }
    }
    
    // Fixed menu items (alternatives) filtered by division + gluten-free eligibility
    const alternatives = config?.config?.alternatives || [];
    const division = getGroupDivision();
    const isGfEligible = camper ? camperNeedsGlutenFree(camper) : true;

    alternatives.filter(a => a.is_active).forEach(alt => {
      // Division filter
      if (alt.divisions && alt.divisions.length > 0 && division) {
        if (!alt.divisions.includes(division)) return;
      }
      // Auto-detect GF items by name — hide from non-eligible campers
      if (isGlutenFreeItem(alt.name) && !isGfEligible) return;

      items.push({
        value: alt.name,
        label: alt.name,
        category: alt.category || 'Other'
      });
    });
    
    return items;
  };

  const noiseValues = ['no', 'none', 'n/a', 'na', 'ni', ''];
  const getRealAllergies = (camper) => {
    if (camper.is_staff) return [];
    return (camper.allergies || []).filter(a => 
      !noiseValues.includes(a.toLowerCase().trim()) && !a.toLowerCase().startsWith('no ')
    );
  };

  // Display-only cleanup for allergy chips: drop filler words, split on separators, dedupe
  const ALLERGY_FILLER = ['yes', 'severe', 'allergies', 'allergy', 'allergic', 'to', 'and', 'the', 'a', 'an', 'of', 'has', 'have', 'very', 'known'];
  const cleanAllergyDisplay = (allergies) => {
    const out = [];
    const seen = new Set();
    for (const entry of allergies || []) {
      const phrases = (entry || '').split(/[,;/]| and | & /i);
      for (const p of phrases) {
        const words = p.trim().split(/\s+/).filter(w => {
          const clean = w.toLowerCase().replace(/[^a-z]/g, '');
          return clean && !ALLERGY_FILLER.includes(clean);
        });
        const term = words.join(' ').replace(/^[^a-zA-Z]+|[^a-zA-Z)]+$/g, '');
        const key = term.toLowerCase();
        if (term && !seen.has(key)) { seen.add(key); out.push(term); }
      }
    }
    return out;
  };

  const camperNeedsGlutenFree = (camper) => {
    const fields = [...(camper.allergies || []), ...(camper.dietary_restrictions || [])];
    const gluten_keywords = ['gluten', 'celiac', 'coeliac'];
    for (const entry of fields) {
      const val = (entry || '').trim().toLowerCase();
      if (!val || noiseValues.includes(val) || val.startsWith('no ')) continue;
      if (gluten_keywords.some(kw => val.includes(kw))) return true;
    }
    return false;
  };

  // ── Allergen-to-item blocking (mirrors backend; backend is authoritative) ──
  const deriveCamperAllergens = (camper) => {
    const tags = new Set();
    if (!allergenData) return tags;
    const entries = [...(camper.allergies || []), ...(camper.dietary_restrictions || [])];
    for (const entry of entries) {
      const val = (entry || '').trim().toLowerCase();
      if (!val) continue;
      for (const [tag, kws] of Object.entries(allergenData.keyword_map || {})) {
        for (const kw of kws) {
          if (kw === 'fish') {
            if (val.replace(/shellfish/g, '').includes('fish')) { tags.add(tag); break; }
          } else if (val.includes(kw)) { tags.add(tag); break; }
        }
      }
    }
    return tags;
  };

  const getItemAllergenTags = (name) => {
    const tags = new Set();
    if (!allergenData) return tags;
    let norm = (name || '').trim();
    if (norm.toUpperCase().startsWith('TRIP:')) norm = norm.slice(5).trim();
    norm = norm.toLowerCase();
    // Auto-detect allergens from the item name itself (additive safety layer)
    const auto = norm.replace(/peanut butter/g, 'peanut').replace(/butternut/g, 'squash').replace(/eggplant/g, 'aubergine').replace(/[a-z]+[- ]?free/g, '');
    for (const [tag, kws] of Object.entries(allergenData.item_name_keywords || {})) {
      for (const kw of kws) {
        if (kw === 'fish') {
          if (auto.replace(/shellfish/g, '').includes('fish')) { tags.add(tag); break; }
        } else if (auto.includes(kw)) { tags.add(tag); break; }
      }
    }
    for (const m of allergenData.item_map || []) {
      const key = (m.item_name || '').trim().toLowerCase();
      if (key && norm.includes(key)) (m.allergens || []).forEach(t => tags.add(t));
    }
    return tags;
  };

  const checkAllergenBlock = (camper, itemName) => {
    if (!allergenData || camper.is_staff) return null;
    const itemTags = getItemAllergenTags(itemName);
    if (itemTags.size === 0) return null;
    const camperTags = deriveCamperAllergens(camper);
    const matched = [...camperTags].filter(t => itemTags.has(t));
    if (matched.length === 0) return null;
    const overrides = (allergenData.overrides || []).filter(o => String(o.person_id) === String(camper.person_id));
    const norm = (itemName || '').trim().toLowerCase();
    const itemCovered = overrides.some(o => o.type === 'item' && o.value && norm.includes(o.value.trim().toLowerCase()));
    if (itemCovered) return null;
    const covered = new Set(overrides.filter(o => o.type === 'allergen').map(o => (o.value || '').toLowerCase()));
    const blocking = matched.filter(t => !covered.has(t));
    return blocking.length > 0 ? blocking : null;
  };

  const handleLunchChange = (camperId, value) => {
    const camper = campers.find(c => c.id === camperId);

    // Name-required override fires ONLY on a true allergen-to-item match
    const blocking = camper ? checkAllergenBlock(camper, value) : null;
    if (blocking) {
      setOverrideName('');
      setOverrideError('');
      setAllergyWarning({
        camperId,
        camperName: `${camper.preferred_name || camper.first_name} ${camper.last_name}`,
        allergies: getRealAllergies(camper),
        matchedAllergens: blocking,
        lunchChoice: value
      });
      return;
    }

    // Safe item — place immediately, no warning, no name
    setOrders(prev => ({ ...prev, [camperId]: value }));
    setSavedOrders(prev => ({ ...prev, [camperId]: false }));
    setOverrides(prev => {
      if (!prev[camperId]) return prev;
      const next = { ...prev };
      delete next[camperId];
      return next;
    });
  };

  const confirmAllergyOrder = () => {
    if (!allergyWarning) return;
    const name = overrideName.trim();
    if (!name) {
      setOverrideError('You must select a different item due to a known allergy.');
      return;
    }
    const at = new Date().toLocaleString('sv-SE', { timeZone: 'America/New_York' }).replace(' ', 'T') + ' EST';
    setOverrides(prev => ({ ...prev, [allergyWarning.camperId]: { by: name, at } }));
    setOrders(prev => ({ ...prev, [allergyWarning.camperId]: allergyWarning.lunchChoice }));
    setSavedOrders(prev => ({ ...prev, [allergyWarning.camperId]: false }));
    setAllergyWarning(null);
    setOverrideName('');
    setOverrideError('');
  };

  const cancelAllergyOrder = () => {
    setAllergyWarning(null);
    setOverrideName('');
    setOverrideError('');
  };

  const [savingAll, setSavingAll] = useState(false);
  const [clearingDay, setClearingDay] = useState(false);

  const handleClearDayOrders = async () => {
    const dateToUse = orderDate || get_est_now_str();
    if (!window.confirm(`Clear ALL orders for ${dateToUse}? This cannot be undone.`)) return;
    
    const password = window.prompt('Enter password to clear orders:');
    if (!password) return;
    
    setClearingDay(true);
    try {
      const res = await api.clearDayOrders(dateToUse, password);
      toast.success(res.data.message || `Cleared orders for ${dateToUse}`);
      // Reset local state
      setOrders({});
      setSavedOrders({});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear orders');
    } finally {
      setClearingDay(false);
    }
  };

  const get_est_now_str = () => {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  };

  const handleSaveCamper = async (camper) => {
    if (cutoffPassed) {
      toast.error('Orders are closed for today');
      return;
    }

    if (!orders[camper.id]) {
      toast.error('Please select a lunch option');
      return;
    }

    setSaving(prev => ({ ...prev, [camper.id]: true }));
    
    try {
      const allOrders = actualCampers
        .filter(c => orders[c.id])
        .map(c => ({
          camper_id: c.id,
          camper_name: `${c.preferred_name || c.first_name} ${c.last_name}`,
          lunch_choice: orders[c.id],
          allergy_override: !!overrides[c.id],
          allergy_override_by: overrides[c.id]?.by || null,
          allergy_override_at: overrides[c.id]?.at || null,
          snapshot_id: c.snapshot_id || null
        }));

      await api.submitOrder({
        group_id: groupAuth.group.id,
        group_name: groupAuth.group.name,
        orders: allOrders,
        counselor_cit_count: counselorCitCount || 0
      }, orderDate || undefined);

      setSavedOrders(prev => ({ ...prev, [camper.id]: true }));
      setCounselorCitSaved(true);
      toast.success(`Saved: ${camper.first_name} - ${orders[camper.id]}`);
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(prev => ({ ...prev, [camper.id]: false }));
    }
  };

  const handleSaveAll = async () => {
    if (cutoffPassed) {
      toast.error('Orders are closed for today');
      return;
    }

    const orderedCampers = actualCampers.filter(c => orders[c.id]);
    if (orderedCampers.length === 0) {
      toast.error('Please select lunch options before saving');
      return;
    }

    setSavingAll(true);
    try {
      const allOrders = orderedCampers.map(c => ({
        camper_id: c.id,
        camper_name: `${c.preferred_name || c.first_name} ${c.last_name}`,
        lunch_choice: orders[c.id],
        allergy_override: !!overrides[c.id],
        allergy_override_by: overrides[c.id]?.by || null,
        allergy_override_at: overrides[c.id]?.at || null,
        snapshot_id: c.snapshot_id || null
      }));

      await api.submitOrder({
        group_id: groupAuth.group.id,
        group_name: groupAuth.group.name,
        orders: allOrders,
        counselor_cit_count: counselorCitCount || 0
      }, orderDate || undefined);

      const newSaved = {};
      orderedCampers.forEach(c => { newSaved[c.id] = true; });
      setSavedOrders(prev => ({ ...prev, ...newSaved }));
      setCounselorCitSaved(true);
      toast.success(`All ${orderedCampers.length} orders saved!`);
    } catch (error) {
      console.error('Save all error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save orders');
    } finally {
      setSavingAll(false);
    }
  };

  const handleLogout = () => {
    logoutGroup();
    navigate('/');
  };

  const actualCampers = campers.filter(c => !c.is_staff);
  const staffMembers = campers.filter(c => c.is_staff);
  const completedCount = actualCampers.filter(c => savedOrders[c.id]).length;
  const isAllComplete = completedCount === actualCampers.length && actualCampers.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#2B4F8C] mx-auto mb-4" />
          <p className="text-slate-600">Loading campers...</p>
        </div>
      </div>
    );
  }

  // Global menu items (no camper filter, used for general display)
  const allMenuItems = getMenuItems();
  const foodOfDay = getFoodOfDay();

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      {/* Header */}
      <header className="bg-[#2B4F8C] text-white p-4 sticky top-0 z-40 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Rolling River" className="w-10 h-10" />
            <div>
              <h1 className="font-bold text-lg">{groupAuth?.group?.name}</h1>
              <p className="text-sm text-blue-200">Lunch Orders</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAllComplete && (
              <Badge className="bg-green-500 text-white px-3 py-1">
                <Check className="w-4 h-4 mr-1" />
                Complete
              </Badge>
            )}
            <a
              href="https://campersnapshot.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/70 hover:text-white px-2 py-1 rounded-lg hover:bg-white/10 transition-colors text-xs font-medium"
              data-testid="campersnapshot-link"
            >
              Back to CamperSnapshot
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/counselor-sop-pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/70 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="counselor-help-btn"
              title="Help Guide"
            >
              <HelpCircle className="w-5 h-5" />
            </a>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Status Banner */}
      {orderDate ? (
        <div className="bg-[#2B4F8C] text-white p-3 text-center" data-testid="custom-date-banner">
          <div className="flex items-center justify-center gap-2">
            <Calendar className="w-5 h-5" />
            <span className="font-semibold">Ordering for: {orderDate}</span>
            <span className="text-sm text-blue-200">(No cutoff enforced for custom dates)</span>
          </div>
        </div>
      ) : !cutoffPassed ? (
        <div className="bg-[#FFC107] text-slate-900 p-3 text-center" data-testid="countdown-banner">
          <div className="flex items-center justify-center gap-2">
            <Clock className="w-5 h-5" />
            <span className="font-semibold">Orders close in:</span>
            <span className="font-mono font-bold text-lg">{formatCountdown(timeUntilCutoff)}</span>
            <span className="text-sm">(9:30 AM EST)</span>
          </div>
        </div>
      ) : (
        <div className="bg-red-600 text-white p-3 text-center" data-testid="cutoff-banner">
          <div className="flex items-center justify-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span className="font-semibold">Orders closed for today (Cutoff: 9:30 AM EST)</span>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="bg-white border-b p-3">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-slate-600">{completedCount} of {actualCampers.length} ordered</span>
            <span className={isAllComplete ? 'text-green-600 font-bold' : 'text-slate-500'}>
              {isAllComplete ? 'All Complete!' : `${Math.round((completedCount / actualCampers.length) * 100) || 0}%`}
            </span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-300 ${isAllComplete ? 'bg-green-500' : 'bg-[#2B4F8C]'}`}
              style={{ width: `${(completedCount / actualCampers.length) * 100 || 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Date Picker for Testing */}
      <div className="bg-white border-b p-3">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 flex-1">
            <Calendar className="w-5 h-5 text-[#2B4F8C]" />
            <label className="text-sm font-semibold text-slate-700">Order Date:</label>
            <input
              type="date"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
              className="border-2 border-slate-300 rounded-md px-3 py-1.5 text-sm focus:border-[#2B4F8C] focus:outline-none"
              data-testid="order-date-picker"
            />
            {orderDate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOrderDate('')}
                className="text-slate-500 hover:text-slate-700 text-xs"
                data-testid="reset-date-btn"
              >
                Reset to Today
              </Button>
            )}
          </div>
          {orderDate && (
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid="custom-date-badge">
                Ordering for: {orderDate}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearDayOrders}
                disabled={clearingDay}
                className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                data-testid="clear-day-orders-btn"
              >
                {clearingDay ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-1" />
                )}
                Clear Day Orders
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-4">
        {campers.length === 0 ? (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6 text-center">
              <User className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <h3 className="font-bold text-amber-800 mb-2">No One Assigned</h3>
              <p className="text-amber-700">
                No campers or staff are assigned to this group yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {/* Counselor & CIT Lunch of the Day count */}
            <Card className={`border-2 transition-all ${counselorCitSaved && counselorCitCount > 0 ? 'border-green-300 bg-green-50' : 'border-[#2B4F8C]/30 bg-[#2B4F8C]/5'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-bold text-[#2B4F8C] text-base">Counselor & CIT — Lunch of the Day</h3>
                    <p className="text-sm text-slate-500 mt-0.5">
                      {foodOfDay ? foodOfDay : 'No special set for this date'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="0"
                      max="99"
                      value={counselorCitCount || ''}
                      onChange={(e) => {
                        setCounselorCitCount(parseInt(e.target.value) || 0);
                        setCounselorCitSaved(false);
                      }}
                      placeholder="0"
                      className="w-20 text-center text-lg font-bold border-2 border-slate-300 rounded-lg px-2 py-2 focus:border-[#2B4F8C] focus:outline-none"
                      data-testid="counselor-cit-count"
                      disabled={cutoffPassed && !orderDate}
                    />
                    {counselorCitSaved && counselorCitCount > 0 && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Staff roster (display only — no ordering) */}
            {staffMembers.length > 0 && (
              <>
                <div className="flex items-center gap-2 pt-2 pb-1" data-testid="staff-section-header">
                  <div className="h-px flex-1 bg-blue-300" />
                  <span className="text-sm font-bold text-[#2B4F8C] uppercase tracking-wider">Staff ({staffMembers.length})</span>
                  <div className="h-px flex-1 bg-blue-300" />
                </div>
                <Card className="border-2 border-blue-200 bg-blue-50/50">
                  <CardContent className="p-4">
                    <div className="flex flex-wrap gap-2">
                      {staffMembers.map((staff) => (
                        <Badge 
                          key={staff.id} 
                          className="bg-[#2B4F8C] text-white text-sm px-3 py-1.5"
                          data-testid={`staff-badge-${staff.id}`}
                        >
                          <User className="w-3.5 h-3.5 mr-1.5" />
                          {staff.preferred_name || staff.first_name} {staff.last_name}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Campers section */}
            {actualCampers.length > 0 && (
              <div className="flex items-center gap-2 pt-4 pb-1" data-testid="campers-section-header">
                <div className="h-px flex-1 bg-slate-300" />
                <span className="text-sm font-bold text-slate-600 uppercase tracking-wider">Campers ({actualCampers.length})</span>
                <div className="h-px flex-1 bg-slate-300" />
              </div>
            )}
            {actualCampers.map((camper) => {
              const menuItems = getMenuItems(camper);
              return (
              <Card 
                key={camper.id} 
                className={`border-2 transition-all ${
                  savedOrders[camper.id] 
                    ? 'border-green-300 bg-green-50' 
                    : camper.allergies?.length > 0 
                      ? 'border-red-200' 
                      : 'border-slate-200'
                }`}
                data-testid={`camper-card-${camper.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Camper Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-slate-900">
                          {camper.preferred_name || camper.first_name} {camper.last_name}
                        </h3>
                        {savedOrders[camper.id] && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      
                      {/* Badges */}
                      <div className="flex flex-wrap gap-2 mt-1">
                        {(() => {
                          const realAllergies = getRealAllergies(camper);
                          const chips = cleanAllergyDisplay(realAllergies);
                          return realAllergies.length > 0 ? (
                            <Badge className="bg-red-600 text-white text-xs" data-testid={`allergy-badge-${camper.id}`}>
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Allergies: {chips.length > 0 ? chips.join(', ') : realAllergies.join(', ')}
                            </Badge>
                          ) : (
                            <span className="text-xs text-slate-400" data-testid={`allergy-none-${camper.id}`}>Allergies: None</span>
                          );
                        })()}
                        {camper.dietary_restrictions?.length > 0 && (
                          <Badge className="bg-amber-500 text-white text-xs" data-testid={`dietary-badge-${camper.id}`}>
                            {camper.dietary_restrictions.filter(r => !['none','no','n/a'].includes(r.toLowerCase().trim())).join(', ')}
                          </Badge>
                        )}
                        {camper.dietary_detail && !['none','no','n/a'].includes(camper.dietary_detail.toLowerCase().trim()) && (
                          <span className="text-xs text-slate-500" data-testid={`dietary-detail-${camper.id}`}>{camper.dietary_detail}</span>
                        )}
                        {overrides[camper.id] && (
                          <Badge className="bg-amber-600 text-white text-xs" data-testid={`allergy-override-info-${camper.id}`}>
                            Allergy override by {overrides[camper.id].by}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Lunch Selection */}
                    <div className="flex items-center gap-2 md:w-80">
                      <Select
                        value={orders[camper.id] || ''}
                        onValueChange={(value) => handleLunchChange(camper.id, value)}
                        disabled={cutoffPassed}
                      >
                        <SelectTrigger 
                          className="h-12 flex-1 border-2 text-base"
                          data-testid={`lunch-select-${camper.id}`}
                        >
                          <SelectValue placeholder="Select lunch..." />
                        </SelectTrigger>
                        <SelectContent>
                          {foodOfDay && (
                            <>
                              <div className="px-2 py-1 text-xs font-bold text-amber-600 bg-amber-50">
                                LUNCH OF THE DAY
                              </div>
                              <SelectItem value={foodOfDay}>
                                {foodOfDay}
                              </SelectItem>
                              <div className="border-t my-1" />
                            </>
                          )}
                          {/* Sandwiches */}
                          {menuItems.filter(i => i.category === 'Sandwiches').length > 0 && (
                            <>
                              <div className="px-2 py-1 text-xs font-bold text-slate-500">
                                SANDWICHES
                              </div>
                              {menuItems.filter(i => i.category === 'Sandwiches').map(item => (
                                <SelectItem key={item.value} value={item.value}>
                                  {item.label}
                                </SelectItem>
                              ))}
                              <div className="border-t my-1" />
                            </>
                          )}
                          {/* Salads */}
                          {menuItems.filter(i => i.category === 'Salads').length > 0 && (
                            <>
                              <div className="px-2 py-1 text-xs font-bold text-slate-500">
                                SALADS
                              </div>
                              {menuItems.filter(i => i.category === 'Salads').map(item => (
                                <SelectItem key={item.value} value={item.value}>
                                  {item.label}
                                </SelectItem>
                              ))}
                              <div className="border-t my-1" />
                            </>
                          )}
                          {/* Bagels */}
                          {menuItems.filter(i => i.category === 'Bagels').length > 0 && (
                            <>
                              <div className="px-2 py-1 text-xs font-bold text-slate-500">
                                BAGELS
                              </div>
                              {menuItems.filter(i => i.category === 'Bagels').map(item => (
                                <SelectItem key={item.value} value={item.value}>
                                  {item.label}
                                </SelectItem>
                              ))}
                              <div className="border-t my-1" />
                            </>
                          )}
                          {/* Other Options */}
                          {menuItems.filter(i => i.category === 'Other').length > 0 && (
                            <>
                              <div className="px-2 py-1 text-xs font-bold text-slate-500">
                                OTHER OPTIONS
                              </div>
                              {menuItems.filter(i => i.category === 'Other').map(item => (
                                <SelectItem key={item.value} value={item.value}>
                                  {item.label}
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>

                      <Button
                        onClick={() => handleSaveCamper(camper)}
                        disabled={cutoffPassed || !orders[camper.id] || saving[camper.id] || savedOrders[camper.id]}
                        className={`h-12 px-4 ${
                          savedOrders[camper.id] 
                            ? 'bg-green-600 hover:bg-green-600' 
                            : 'bg-[#2B4F8C] hover:bg-[#1E3A6B]'
                        }`}
                        data-testid={`save-btn-${camper.id}`}
                      >
                        {saving[camper.id] ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : savedOrders[camper.id] ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <Save className="w-5 h-5" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )})}
          </div>
        )}
      </main>

      {/* Save All Sticky Footer */}
      {actualCampers.length > 0 && !cutoffPassed && (
        <div className="sticky bottom-0 z-30 bg-white border-t-2 border-[#2B4F8C] shadow-[0_-4px_12px_rgba(0,0,0,0.1)] p-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="text-sm text-slate-600">
              <span className="font-semibold text-[#2B4F8C]">{actualCampers.filter(c => orders[c.id]).length}</span> of {actualCampers.length} selected
              {actualCampers.filter(c => orders[c.id] && !savedOrders[c.id]).length > 0 && (
                <span className="text-amber-600 ml-2">
                  ({actualCampers.filter(c => orders[c.id] && !savedOrders[c.id]).length} unsaved)
                </span>
              )}
            </div>
            <Button
              onClick={handleSaveAll}
              disabled={savingAll || actualCampers.filter(c => orders[c.id]).length === 0 || isAllComplete}
              className={`h-12 px-8 text-lg font-bold ${
                isAllComplete
                  ? 'bg-green-600 hover:bg-green-600'
                  : 'bg-[#2B4F8C] hover:bg-[#1E3A6B]'
              }`}
              data-testid="save-all-btn"
            >
              {savingAll ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Saving...
                </>
              ) : isAllComplete ? (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  All Saved
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save All Orders
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Allergy Conflict Warning Modal */}
      {allergyWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="allergy-warning-modal">
          <Card className="w-full max-w-md border-2 border-red-500 shadow-2xl">
            <CardHeader className="bg-red-600 text-white pb-4">
              <CardTitle className="flex items-center gap-2 text-white text-xl">
                <AlertTriangle className="w-6 h-6" />
                Allergy Warning
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-lg font-semibold text-slate-900">
                {allergyWarning.camperName}
              </p>
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                <p className="text-sm font-bold text-red-800 mb-2">Known Allergies:</p>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    const chips = cleanAllergyDisplay(allergyWarning.allergies);
                    return (chips.length > 0 ? chips : allergyWarning.allergies).map((a, i) => (
                      <Badge key={i} className="bg-red-600 text-white text-sm px-3 py-1">{a}</Badge>
                    ));
                  })()}
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <span className="font-bold">Selected:</span> {allergyWarning.lunchChoice}
                </p>
                {allergyWarning.matchedAllergens?.length > 0 && (
                  <p className="text-sm text-red-700 font-bold mt-1" data-testid="allergy-match-detail">
                    This item contains: {allergyWarning.matchedAllergens.join(', ')} — matching this camper's allergy.
                  </p>
                )}
              </div>
              <p className="text-sm text-slate-600">
                This item matches a known allergy for this camper. Pick a different item, or type your name to take responsibility and override.
              </p>
              <div>
                <label className="text-sm font-bold text-slate-700 block mb-1" htmlFor="allergy-override-name">
                  Your name (required to override)
                </label>
                <input
                  id="allergy-override-name"
                  type="text"
                  value={overrideName}
                  onChange={(e) => { setOverrideName(e.target.value); if (overrideError) setOverrideError(''); }}
                  placeholder="Type your name to approve this order"
                  className="w-full h-11 px-3 border-2 border-slate-300 rounded-lg text-base focus:border-red-500 focus:outline-none"
                  data-testid="allergy-override-name-input"
                />
              </div>
              {overrideError && (
                <div className="bg-red-100 border-2 border-red-400 rounded-lg p-3" data-testid="allergy-override-error">
                  <p className="text-sm font-bold text-red-800">{overrideError}</p>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 h-12 text-base"
                  onClick={cancelAllergyOrder}
                  data-testid="allergy-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 h-12 text-base bg-red-600 hover:bg-red-700 text-white"
                  onClick={confirmAllergyOrder}
                  data-testid="allergy-confirm-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Confirm Order
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default OrderForm;
